import React, { useState, Suspense, useEffect, memo, useCallback } from "react";
import { SectionProps } from "../Section";
import {
	Grid,
	Button,
	Dialog,
	DialogTitle,
	DialogActions,
	LinearProgress,
	List,
	ListItem,
	Typography,
	CircularProgress
} from "@material-ui/core";
import { useFetcher } from "@rest-hooks/core";
import { makeStyles } from "@material-ui/core/styles";
import Link from "@material-ui/core/Link";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import ClearIcon from "@material-ui/icons/Clear";
import { useTenantImage, useThemeComponent } from "@ploy-ui/tenants";

import { addRegisteredSectionLayout } from "../sectionLayoutDescriptions";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";
import {
	SigningDocumentResource,
	useResourceWithInvalidate,
	ApplicationSignerResource,
	UploadedDocumentResource,
	VendorDocumentGroupResource,
	ID,
	DocumentSettingsResource
} from "@ploy-lib/rest-resources";
import { Dropzone } from "@ploy-ui/form-fields";
import { useAppLoad } from "../../appLoadContext";
import { useResource } from "@rest-hooks/core";
import { legacyApiResourceUrl } from "@ploy-lib/core";
import { useSearchParams } from "react-router-dom";
import DescriptionIcon from "@material-ui/icons/Description";
import { ImageFit } from "@ploy-ui/core";
import clsx from "clsx";
import { LabelComponent } from "../../field";

const useStyles = makeStyles(
	theme => ({
		dropZoneArea: {
			borderWidth: "1px",
			borderStyle: "dashed",
			margin: theme.spacing(0.5),
			color: "black",
			backgroundColor: theme.palette.common.white,
			borderColor: theme.palette.primary.main
		},
		dropZoneInner: {
			gap: theme.spacing(2),
			justifyContent: "center",
			alignItems: "center",
			padding: theme.spacing(2, 0),
			width: "inherit"
		},
		circleIconContainer: { marginLeft: theme.spacing(2), padding: "unset" },
		clearIcon: {
			float: "right"
		},
		dropZoneTextContainer: { padding: theme.spacing(0), margin: "unset" },
		dropZoneTextUpper: { fontSize: "16px", fontWeight: 771, color: "#00002B" },
		dropZoneTextLower: { fontSize: "14px", fontWeight: 771, color: "#404060" },
		documentListSection: {},
		documentListCard: {
			border: "1px",
			borderStyle: "solid",
			alignItems: "center",
			borderRadius: "16px",
			backgroundColor: "white",
			padding: theme.spacing(2, 2),
			margin: theme.spacing(1, 0)
		},
		documentListHeader: { margin: theme.spacing(1, 0), fontWeight: 775 },
		sectionTitle: { margin: theme.spacing(0.5) },
		fallbackAnimation: {
			animation: `$fading 1.2s ease-in-out infinite`
		},
		"@keyframes fading": {
			"0%": { opacity: 0.2 },
			"50%": { opacity: 1 },
			"100%": { opacity: 0.2 }
		}
	}),
	{ name: "DployDocumentDropzoneSection" }
);

const intlMessages = defineMessages({
	uploadTitle: {
		id: "template-form.sections.document-dropzone.title",
		description: "Upload contract",
		defaultMessage: "Upload contract"
	},
	uploadHelpertext: {
		id: "template-form.sections.document-dropzone.helpertext",
		description: "Upload the signed contract if you have it.",
		defaultMessage:
			"If you have a signed copy of the contract you can upload it here. If you do not have a signed copy of the contract it can be sent later."
	}
});

const uploadDocument = (
	file: File,
	documentType: string | null,
	customerContext: string | null
) => {
	const formData = new FormData();
	formData.append("file", file, file.name);
	if (documentType) formData.append("documentType", documentType);
	if (customerContext) formData.append("customerContext", customerContext);

	return fetch(legacyApiResourceUrl("DocumentGrid/Upload"), {
		method: "POST",
		body: formData
	});
};
// @todo: This is a legacy API call, and should be replaced with a REST call using the rest-hooks library.
const deleteDocument = documentId => {
	return fetch(legacyApiResourceUrl("DocumentGrid/DeleteUploadedDocument"), {
		method: "POST",
		headers: new Headers({
			"Content-type": "application/json"
		}),
		body: JSON.stringify({
			documentId: documentId
		})
	});
};

const ConfirmDialog = ({ open, text, onConfirm, onDeny }) => {
	return (
		<Dialog open={open}>
			<DialogTitle>{text}</DialogTitle>
			<DialogActions>
				<Button onClick={onDeny} color="primary">
					<FormattedMessage
						id="template-form.sections.document-upload.confirm-no"
						defaultMessage="No"
					/>
				</Button>
				<Button onClick={onConfirm} color="primary">
					<FormattedMessage
						id="template-form.sections.document-upload.confirm-yes"
						defaultMessage="Yes"
					/>
				</Button>
			</DialogActions>
		</Dialog>
	);
};

addRegisteredSectionLayout({
	name: "DocumentDropzoneSection",
	displayName: "DocumentDropzoneSection",
	settings: {
		editableOptions: {
			allowEditSameSession: true
		}
	}
});

interface DocumentDropzoneSectionProps extends SectionProps {
	useDropzone?: boolean;
}

const DocumentDropzoneSection = (props: DocumentDropzoneSectionProps) => {
	const classes = useStyles(props);
	const intl = useIntl();
	const { className, onClick } = props;

	const [showHelperText, setShowHelperText] = useState(false);

	const toggleHelperText = useCallback(
		(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			setShowHelperText(show => !show);
			e.stopPropagation();
		},
		[]
	);

	return (
		<Grid container className={className} onClick={onClick}>
			<Grid item xs={12} className={classes.sectionTitle}>
				<LabelComponent
					labelText={
						<Typography variant="caption">
							{intl.formatMessage(intlMessages.uploadTitle)}
						</Typography>
					}
					trimmedHelperText={intl.formatMessage(intlMessages.uploadHelpertext)}
					showHelperText={showHelperText}
					toggleHelperText={toggleHelperText}
				/>
			</Grid>
			<Suspense fallback={<DropzoneLoadingFallback />}>
				<DropzoneComponent />
			</Suspense>
		</Grid>
	);
};

DocumentDropzoneSection.displayName = "DployDocumentDropzoneSection";

export { DocumentDropzoneSection };

const DropzoneLoadingFallback = () => {
	const classes = useStyles();
	return (
		<Grid
			container
			justifyContent="space-between"
			alignItems="center"
			id="DropzoneSuspenseFallback"
			style={{ height: 80, borderRadius: 4 }}
			className={clsx(classes.fallbackAnimation, classes.dropZoneArea)}
		>
			<CircularProgress style={{ margin: "0 auto" }} />
		</Grid>
	);
};

const DropzoneComponent = memo(() => {
	const docIcon = useTenantImage("Factoring");
	const intl = useIntl();
	const { applicationNumber, id: appSessionId } = useAppLoad();

	const isDirectUser = true;

	const [searchParams, _] = useSearchParams();
	const customerContext = searchParams.get("customerContext");

	const refetchSigningDocuments = useFetcher(SigningDocumentResource.list());
	const refetchSigners = useFetcher(ApplicationSignerResource.list());

	const documentList = useResourceWithInvalidate(
		UploadedDocumentResource.list(),
		appSessionId ? { applicationNumber, customerContext } : null
	);

	const [initialDocuments, setInitialDocuments] =
		useState<(ID | undefined)[]>();

	useEffect(() => {
		if (initialDocuments) return;
		setInitialDocuments(documentList?.map(x => x.documentId) ?? []);
	}, [documentList, initialDocuments]);

	const refetchDocuments = useFetcher(UploadedDocumentResource.list());
	// only deletes from the cache, we don't have a method that overrides the delete method in rest-hooks
	const deleteDocumentFromResource = useFetcher(
		UploadedDocumentResource.delete()
	);

	const fetchDocGroups = !isDirectUser && !!appSessionId;

	const documentGroups = useResource(
		VendorDocumentGroupResource.list(),
		fetchDocGroups ? { applicationNumber } : null
	);

	// Fetch hooks end
	const [selectedGroup, setSelectedGroup] =
		useState<VendorDocumentGroupResource | null>(null);

	useEffect(() => {
		let defaultGroup = documentGroups?.find(dg => dg.isDefaultGroup);
		if (defaultGroup) setSelectedGroup(defaultGroup);
	}, [documentGroups]);

	const classes = useStyles();

	const [errorMessages, setErrorMessages] = useState<string[] | undefined>([]);
	const ThemeAddDocumentUploadIcon = useThemeComponent("AddDocumentUpload");

	const AddDocumentUploadIcon = ThemeAddDocumentUploadIcon ?? AddCircleIcon;

	const filteredDocumentGroups =
		documentGroups &&
		documentGroups.filter(
			dg =>
				dg.allowMultipleUploads ||
				!(
					documentList &&
					documentList.some(d => d.documentType === dg.documentCode)
				)
		);

	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
	const [selectedDocument, setSelectedDocument] =
		useState<UploadedDocumentResource | null>(null);
	const [uploadLoading, setUploadLoading] = useState(false);

	useEffect(() => {
		if (filteredDocumentGroups && filteredDocumentGroups.length === 1)
			setSelectedGroup(filteredDocumentGroups[0]);
		else if (
			selectedGroup &&
			filteredDocumentGroups &&
			!filteredDocumentGroups.some(
				dg => dg.documentCode === selectedGroup.documentCode
			)
		)
			setSelectedGroup(null);
	}, [filteredDocumentGroups, selectedGroup]);

	const allowedFileTypes = useResource(DocumentSettingsResource.detail(), {
		method: "GET"
	}).allowedFileTypes.map(x => `.${x} `); // Add "." -prefix to each fileType

	return (
		<>
			<Grid container style={{}}>
				<Grid item xs={12}>
					<Dropzone
						className={classes.dropZoneArea}
						disabled={uploadLoading}
						label={
							<Grid
								item
								container
								xs={12}
								className={classes.dropZoneInner}
								direction="row"
								wrap="nowrap"
							>
								<Grid
									container
									item
									xs={1}
									className={classes.circleIconContainer}
								>
									<AddDocumentUploadIcon
										style={{
											width: "40px",
											height: "40px"
										}}
									/>
								</Grid>
								<Grid
									item
									container
									xs={"auto"}
									className={classes.dropZoneTextContainer}
									justifyContent="center"
									alignItems="flex-start"
									direction="column"
								>
									<Grid item>
										<Typography
											variant="body1"
											className={classes.dropZoneTextUpper}
										>
											<FormattedMessage
												id="template-form.section.Document-dropzone-upload.UploadText"
												defaultMessage="Velg fil"
												description="Logo"
											/>
										</Typography>
									</Grid>
									<Grid item className={classes.dropZoneTextLower}>
										<Typography
											variant="caption"
											className={classes.dropZoneTextLower}
										>
											<FormattedMessage
												id="template-form.section.Document-dropzone.ValidFiles"
												defaultMessage="Supported files {validFileTypes}"
												description="validFileTypes"
												values={{
													validFileTypes: allowedFileTypes
												}}
											/>
										</Typography>
									</Grid>
								</Grid>
							</Grid>
						}
						files={[]}
						imgBackground="#FFFFFF"
						accept={allowedFileTypes}
						maxSize={20 ** 19}
						onChange={files => {
							setUploadLoading(true);
							uploadDocument(
								files[0] as File,
								selectedGroup && selectedGroup!.documentCode,
								customerContext
							).then(response => {
								response
									.json()
									.then(result => {
										if (result.error) {
											setErrorMessages([result.error]);
										} else {
											setErrorMessages([]);
										}
									})
									.catch(error => {
										setErrorMessages([
											intl.formatMessage({
												id: "template-form.sections.document-upload.genericuploaderror",
												defaultMessage: "Det skjedde en feil."
											})
										]);
									});
								setUploadLoading(false);
								if (selectedGroup && selectedGroup.mustSign) {
									refetchSigningDocuments({ applicationNumber });
									refetchSigners({ applicationNumber, signerType: 0 });
								}
								refetchDocuments({ applicationNumber, customerContext });
							});
						}}
						onError={messages => setErrorMessages(messages)}
					/>
				</Grid>
				<Grid item style={{ color: "#f44336" }}>
					{errorMessages}
				</Grid>
			</Grid>
			{documentList && documentList.length > 0 ? (
				<Grid item container xs={12} style={{ background: "transparent" }}>
					<Suspense fallback={<LinearProgress />}>
						<Grid xs={12} item>
							<Typography
								variant="body1"
								className={classes.documentListHeader}
							>
								<FormattedMessage
									id="template-form.sections.document-dropzone-upload.uploades-documents-header"
									description="Label for Uploaded documents Button"
									defaultMessage="Uploaded"
								/>
							</Typography>
						</Grid>
						<List style={{ width: "100%", padding: "unset" }}>
							{documentList.map(x => (
								<ListItem
									style={{
										padding: "unset",
										width: "100%"
									}}
								>
									<Grid
										container
										item
										xs={12}
										className={classes.documentListCard}
										style={{}}
									>
										<Grid item xs={1}>
											{docIcon ? (
												<ImageFit src={docIcon} alt="document Icon" />
											) : (
												<DescriptionIcon />
											)}
										</Grid>
										<Grid
											item
											container
											xs={10}
											direction="column"
											style={{
												alignSelf: "center",
												overflow: "hidden",
												textOverflow: "ellipsis"
											}}
										>
											<Link
												href={legacyApiResourceUrl(
													"/DocumentGrid/GetDocument" +
														"?documentId=" +
														x.documentId
												)}
												style={{ width: "inherit" }}
												className="file-upload__document-name"
												underline="hover"
											>
												<Typography
													variant="body1"
													noWrap
													style={{ fontSize: "18px", color: "#00002B" }}
												>
													{x.filename.split(".")[0]}
												</Typography>
												<Typography
													variant="caption"
													style={{ fontSize: "14px", color: "#404060" }}
												>
													{Math.round(x.fileSize / 1000)} kB
												</Typography>
											</Link>
										</Grid>
										<Grid item xs={1}>
											<ClearIcon
												className={classes.clearIcon}
												onClick={() => {
													setSelectedDocument(x);
													setOpenConfirmDialog(true);
												}}
												style={{ cursor: "pointer" }}
											/>
										</Grid>
									</Grid>
								</ListItem>
							))}
						</List>
					</Suspense>
				</Grid>
			) : null}

			<ConfirmDialog
				open={openConfirmDialog}
				text={
					<FormattedMessage
						id="template-form.sections.document-upload.delete-document-confirm"
						description="Confirm deletion of uploaded document"
						defaultMessage="Er du sikker på at du vil slette dokumentet?"
					/>
				}
				onDeny={() => setOpenConfirmDialog(false)}
				onConfirm={() => {
					setOpenConfirmDialog(false);
					deleteDocument(selectedDocument!.documentId).then(() => {
						deleteDocumentFromResource({
							documentId: selectedDocument!.documentId
						});
						refetchSigningDocuments({ applicationNumber });
						refetchSigners({ applicationNumber, signerType: 0 });
						refetchDocuments({ applicationNumber });
					});
				}}
			/>
		</>
	);
});
